// src/main.ts

// Suppress ResizeObserver errors that are common in development with complex UI components
// These errors don't affect functionality but can clutter the console
const suppressResizeObserverErrors = () => {
  // Catch ResizeObserver loop errors
  const originalError = window.onerror
  window.onerror = function(message, source, lineno, colno, error) {
    if (message && typeof message === 'string' && 
        message.includes('ResizeObserver loop completed with undelivered notifications')) {
      // Suppress this specific error
      return true
    }
    // Call original error handler for other errors
    if (originalError) {
      return originalError(message, source, lineno, colno, error)
    }
    return false
  }

  // Also handle unhandled promise rejections
  const originalRejection = window.onunhandledrejection
  window.onunhandledrejection = (event) => {
    if (event.reason && event.reason.message && 
        typeof event.reason.message === 'string' &&
        event.reason.message.includes('ResizeObserver loop completed with undelivered notifications')) {
      event.preventDefault()
      return
    }
    if (originalRejection) {
      originalRejection.call(window, event)
    }
  }

  // Additional catch for ResizeObserver errors that might be thrown directly
  const originalConsoleError = console.error
  console.error = function(...args) {
    const message = args[0]
    if (typeof message === 'string' && 
        message.includes('ResizeObserver loop completed with undelivered notifications')) {
      // Don't log these specific errors
      return
    }
    originalConsoleError.apply(console, args)
  }
}

// Initialize error suppression immediately
suppressResizeObserverErrors()

import "core-js/stable";
import "regenerator-runtime/runtime";
import "./styles/style.css";
import "vue-datetime/dist/vue-datetime.css";
import "mapbox-gl/dist/mapbox-gl.css";


import App from "./app.vue";
import Datepicker from "vue3-datepicker";
// import IdleVue from "idle-vue";
import JsonEditorVue from "json-editor-vue";
import { createApolloProvider } from "@vue/apollo-option";
import { createApp } from "vue";
import { apolloClient, filesRoot } from "./services/vue-apollo";
import filters from "./services/filters";
import { initializeAuth } from "./services/auth"; // Import the initialization function for auth
import router from "./router";
import { setGlobalConfig } from "./config/config.js";
import { validateConfig } from "./config/app-config";
import snackbarPlugin from "./plugins/snackbar";
import { startRemoteLogging } from "./services/remote-logger";
import { installDevAssetProxy } from "./services/dev-asset-proxy";
import { installLayoutDiag } from "./services/dev-layout-diag";
import store from "./store/store";
import vuetify from "@/plugins/vuetify";

let config = null;

// Start shipping browser logs to CloudWatch before anything else runs, so early
// failures (config load, auth bootstrap) are captured too. The console itself is
// already intercepted by the inline guard in public/index.html; this drains what
// it collected and keeps draining. Safe in dev: the guard only installs itself in
// production builds, so there is simply nothing buffered to send.
startRemoteLogging();

const app = createApp(App);

export const loadConfig = async () => {
  try {
    const response = await fetch("/config/config.json");
    if (!response.ok) {
      throw new Error(`Get config error: ${response.status}`);
    }
    config = await response.json();
    validateConfig(config);
    setGlobalConfig(config);
  } catch (error) {
    console.error("Failed to load config:", error);
    throw error;
  }
};

const initializeApp = async () => {
  try {
    // Dev only, and a no-op in production builds. Routes S3/CDN asset requests through the
    // dev server so their origin allowlists -- which cannot include a developer machine --
    // stop discarding responses. Installed before anything fetches.
    installDevAssetProxy();
    // Dev only, and only with ?diag=1: draws the toolbar measurements onto the page so they
    // can be screenshotted from a browser no automation can drive here.
    installLayoutDiag();

    await loadConfig();
    initializeAuth();
  } catch (error) {
    console.error("Failed to initialize the app:", error);
    const loading = document.getElementById("loading");
    if (loading) {
      loading.innerHTML =
        "<h2>Failed to load configuration. Please try again later.</h2>";
    }
    return;
  }

  // const eventsHub = new Vue();

  // // Install Vue plugins
  // app.use(IdleVue, {
  //   eventEmitter: eventsHub,
  //   idleTime: 1000 * 60 * 15, // 15 minutes
  //   store: store,
  //   startAtIdle: false,
  // });

  app.config.globalProperties.$filesRoot = filesRoot;
  const apolloProvider = createApolloProvider({
    defaultClient: apolloClient,
  });

  app.config.globalProperties.$filters = filters;

  app.use(router);
  app.use(store);
  app.use(vuetify);
  app.use(apolloProvider);
  app.use(JsonEditorVue);
  app.use(snackbarPlugin, { store });

  app.component("datetime-selector", Datepicker);

  app.mount("#app");

  const loading = document.getElementById("loading");
  if (loading) {
    loading.style.display = "none";
  }
};

initializeApp();
